import { apiClient } from './client';
import type { TicketTask, TaskSummary, MyTask, Ticket } from '../types';

/** Task-level view: the task, its notes, the parent's headline, and what the caller may do. */
export interface TaskNotesView {
  task: TicketTask;
  notes: any[];
  ticket: { id: string; ticketNumber: string; title: string; status: string };
  canManage: boolean;
  canAdminister: boolean;
}

export const tasksApi = {
  list: async (ticketId: string) => {
    const res = await apiClient.get(`/tickets/${ticketId}/tasks`);
    return res.data.data as { tasks: TicketTask[]; summary: TaskSummary };
  },

  create: async (ticketId: string, data: { title: string; description?: string | null; assignedToId?: string | null }) => {
    const res = await apiClient.post(`/tickets/${ticketId}/tasks`, data);
    return (res.data.data.tasks as TicketTask[])[0];
  },

  /** Add (some of) a template's items to a ticket. Omit itemIds for all of them. */
  addFromTemplate: async (ticketId: string, templateId: string, itemIds?: string[]) => {
    const res = await apiClient.post(`/tickets/${ticketId}/tasks`, { templateId, ...(itemIds ? { itemIds } : {}) });
    return res.data.data.tasks as TicketTask[];
  },

  update: async (
    ticketId: string,
    taskId: string,
    data: { title?: string; description?: string | null; assignedToId?: string | null; sortOrder?: number },
  ) => {
    const res = await apiClient.put(`/tickets/${ticketId}/tasks/${taskId}`, data);
    return res.data.data.task as TicketTask;
  },

  notes: async (ticketId: string, taskId: string) => {
    const res = await apiClient.get(`/tickets/${ticketId}/tasks/${taskId}/notes`);
    return res.data.data as TaskNotesView;
  },

  addNote: async (ticketId: string, taskId: string, content: string, includeContent?: boolean) => {
    const res = await apiClient.post(`/tickets/${ticketId}/tasks/${taskId}/notes`, {
      content, ...(includeContent === false ? { includeContent: false } : {}),
    });
    return res.data.data.message;
  },

  /** The note is required — the server refuses an empty one. */
  complete: async (ticketId: string, taskId: string, note: string, includeContent?: boolean) => {
    const res = await apiClient.post(`/tickets/${ticketId}/tasks/${taskId}/complete`, {
      note, ...(includeContent === false ? { includeContent: false } : {}),
    });
    return res.data.data as { task: TicketTask; message: any };
  },

  reopen: async (ticketId: string, taskId: string, note?: string) => {
    const res = await apiClient.post(`/tickets/${ticketId}/tasks/${taskId}/reopen`, note ? { note } : {});
    return res.data.data as { task: TicketTask };
  },

  /** Create a full ticket from a task (staff). The task links to it and follows its state. */
  spawnTicket: async (
    ticketId: string,
    taskId: string,
    data: { title?: string; description?: string; assignedToId?: string | null; assignedTeamId?: string | null; priority?: string; type?: string } = {},
  ) => {
    const res = await apiClient.post(`/tickets/${ticketId}/tasks/${taskId}/ticket`, data);
    return res.data.data as { task: TicketTask; ticket: Ticket };
  },

  /** Link an existing ticket to a task, or null to unlink (staff). */
  link: async (ticketId: string, taskId: string, linkedTicketId: string | null) => {
    const res = await apiClient.put(`/tickets/${ticketId}/tasks/${taskId}/link`, { linkedTicketId });
    return res.data.data.task as TicketTask;
  },

  remove: async (ticketId: string, taskId: string) => {
    await apiClient.delete(`/tickets/${ticketId}/tasks/${taskId}`);
  },

  /** The caller's tasks across the company (open by default). */
  mine: async (status: 'open' | 'completed' | 'all' = 'open') => {
    const res = await apiClient.get('/tasks/mine', { params: { status } });
    return res.data.data as { tasks: MyTask[]; openCount: number };
  },
};
